// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@bg/App/Components/Notify";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { parseBoolean } from "@bg/XDL/Utilities";
import { createParserOptions } from "@bg/XDL/Utilities/parser";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage, { siteMessageWithCustomParserOptions } from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Typography from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";

interface OwnProps {}

const mapStateToProps = (state: CustomAppState) => {
    const pageType = getCurrentPage(state).type;
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const billTo = getCurrentBillToState(state).value;
    const shipTo = getCurrentShipToState(state).value;
    const customerSettings = getSettingsCollection(state).customerSettings;
    const billToState = getBillToState(state, cartState.value?.billToId);
    const shipToState = getShipToState(state, cartState.value?.shipToId);
    const isBillingAddressUpdateRequired = state.pages.onePageCheckout.isBillingAddressUpdateRequired;
    const isBillingAddressEditing = state.xOnePageCheckout.isBillingAddressEditing;
    const isShippingAddressUpdateRequired = state.pages.onePageCheckout.isShippingAddressUpdateRequired;
    const isShippingAddressEditing = state.xOnePageCheckout.isShippingAddressEditing;
    const isUpdatingCheckout = state.xOnePageCheckout.isUpdatingCheckout;

    const billToIsNotValid =
        billToState?.value && !(isBillingAddressUpdateRequired || isBillingAddressEditing)
            ? !billToState.value.address1 ||
              !billToState.value.city ||
              !billToState.value.state ||
              !billToState.value.postalCode
            : false;

    const shipToIsNotValid =
        cartState?.value?.fulfillmentMethod === FulfillmentMethod.Ship &&
        shipToState?.value &&
        !(isShippingAddressUpdateRequired || isShippingAddressEditing)
            ? !shipToState.value.address1 ||
              !shipToState.value.city ||
              !shipToState.value.state ||
              !shipToState.value.postalCode
            : false;

    return {
        pageType,
        isLoading: cartState.isLoading || isUpdatingCheckout,
        cart: cartState.value,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        placeOrderErrorMessage: state.pages.onePageCheckout.placeOrderErrorMessage,
        canUpdateShipToAddress:
            billTo?.isGuest ||
            shipTo?.oneTimeAddress ||
            customerSettings.allowBillToAddressEdit ||
            customerSettings.allowShipToAddressEdit,
        billToIsNotValid,
        shipToIsNotValid,
        isUpdatingCheckout,
        cartStateIsLoading: cartState.isLoading,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CheckoutMessagesStyles {
    container?: InjectableCss;
}

export const onePageCheckoutMessagesStyles: CheckoutMessagesStyles = {};

const styles = onePageCheckoutMessagesStyles;
const OnePageCheckoutMessages: React.FC<Props> = ({
    isLoading,
    cart,
    cartPageUrl,
    placeOrderErrorMessage,
    canUpdateShipToAddress,
    billToIsNotValid,
    shipToIsNotValid,
}) => {
    const history = useHistory();

    const goToCartPage = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        history.push(cartPageUrl!);
    };

    if (isLoading) {
        return null;
    }

    const parserOptions = createParserOptions(goToCartPage);
    return (
        <>
            <StyledWrapper {...styles.container}>
                {cart?.hasInsufficientInventory && (
                    <Notify
                        messageType="danger"
                        body={
                            <Typography data-test-selector="onePageCheckout_availabilityErrorMessage">
                                {siteMessageWithCustomParserOptions(
                                    "ReviewAndPay_NotEnoughInventoryForPickup",
                                    parserOptions,
                                )}
                            </Typography>
                        }
                    />
                )}
                {placeOrderErrorMessage && (
                    <Notify
                        messageType="danger"
                        body={
                            <Typography data-test-selector="onePageCheckout_placeOrderErrorMessage">
                                {placeOrderErrorMessage}
                            </Typography>
                        }
                    />
                )}
                {cart?.taxFailureReason === "InvalidAddress" && canUpdateShipToAddress && (
                    <Notify
                        messageType="danger"
                        body={
                            <Typography>
                                {siteMessage("ReviewAndPay_InvalidAddress_CannotCalculateTax_UpdateAddress")}
                            </Typography>
                        }
                    />
                )}
                {cart?.taxFailureReason === "InvalidAddress" && !canUpdateShipToAddress && (
                    <Notify
                        messageType="danger"
                        body={<Typography>{siteMessage("ReviewAndPay_InvalidAddress_CannotCalculateTax")}</Typography>}
                    />
                )}
                {cart?.isAwaitingApproval && (
                    <Notify
                        messageType="info"
                        body={translate("Approving Order for {0}", cart.initiatedByUserName?.toUpperCase())}
                    />
                )}
                {cart?.properties && cart?.properties?.erpSystemIsDown === "true" && (
                    <Notify messageType="danger" body={siteMessage("Custom_erpSystemIsDown")} />
                )}
                {billToIsNotValid && (
                    <Notify messageType="danger" body={siteMessage("Custom_yourBillToAddressIsNotValid")} />
                )}
                {shipToIsNotValid && (
                    <Notify messageType="danger" body={siteMessage("Custom_yourShipToAddressIsNotValid")} />
                )}
                {/*
                    XNG-Changes: BG-2900
                */}
                {parseBoolean(cart?.properties.isCreditCardDefaultPayment) && (
                    <Notify messageType="danger" body={siteMessage("Custom_Cart_CreditCardDefaultPayment")} />
                )}
            </StyledWrapper>
        </>
    );
};

export default connect(mapStateToProps)(OnePageCheckoutMessages);
